import { action, computed } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { UserSummary } from "@shared/models/user"
import { objectId } from "@shared/util/object-id"
import first from "lodash-es/first"

interface Signature {
  Element: any
  Args: {
    userSummary?: UserSummary
    invitedUser?: boolean
    email?: string
    avatarSizeClass:
      | "user-avatar--xxl"
      | "user-avatar--lg"
      | "user-avatar--md"
      | "user-avatar--sm"
      | "user-avatar--xs"
    enableTooltip?: boolean
  }
  Blocks: {
    default?: any
  }
}

export default class UserAvatar extends Component<Signature> {
  @service basicTooltip

  @tracked elementId
  @tracked showTooltip = false

  constructor(owner, args) {
    super(owner, args)
    this.elementId = `guid-${objectId()}`
  }

  // @computed("args.userSummary.attributes.avatarColor")
  get cssClasses() {
    return `user-avatar ${this.args.avatarSizeClass}`
  }

  get avatarColor() {
    return this.args.userSummary ? this.args.userSummary.attributes.avatarColor : "white"
  }

  @computed("args.userSummary.attributes.{firstName,lastName}")
  get initials() {
    if (this.args.userSummary) {
      let firstI = first(this.args.userSummary.attributes.firstName || " ")
      let lastI = first(this.args.userSummary.attributes.lastName || " ")
      return `${firstI}${lastI}`
    } else {
      return ""
    }
  }

  @computed("args.email", "args.userSummary.attributes.{firstName,lastName}")
  get tooltipText() {
    if (this.args.invitedUser) {
      return this.args.email
    } else if (this.args.userSummary) {
      return `${this.args.userSummary?.attributes.firstName} ${this.args.userSummary?.attributes.lastName}`
    } else {
      return ""
    }
  }

  @action onMouseEnter() {
    if (this.args.enableTooltip) {
      this.basicTooltip.show({
        targetId: this.elementId,
        message: this.tooltipText,
      })
    }
  }

  @action onMouseLeave() {
    if (this.args.enableTooltip) {
      this.basicTooltip.hide({ targetId: this.elementId })
    }
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "User::Avatar": typeof UserAvatar
  }
}
